import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormGroup, NgControl, ValidationErrors } from '@angular/forms';
import { IUser } from '@app/interfaces/IUser';
import { IVehicle } from '@app/interfaces/IVehicle';
import { IGroupWithIds } from '@app/main/components/resourceManagement/groups/_models/IGroupWithIds';
import { GroupsHierarchyHelper } from '@app/main/components/resourceManagement/groups/utils/groups-hierarchy-helper';
import { ICameraConfiguration } from '@app/main/modules/foundation/_routes/rv-lite/config/_models/ICameraConfiguration';
import { FoundationVehiclesService } from '@app/main/modules/foundation/_services/vehicle.service';
import { TranslationService } from '@services/translation.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class FormValidationService {

    constructor(
        private readonly translationService: TranslationService
    ) {
    }

    getErrorLabel(errors: ValidationErrors): string {

        if (errors["required"]) {
            return this.translationService.t("FormValidationService.Required");
        }

        if (errors["minlength"]) {
            return this.translationService.t("FormValidationService.MinLength", errors["minlength"].requiredLength);
        }

        if (errors["maxlength"]) {
            return this.translationService.t("FormValidationService.MaxLength", errors["maxlength"].requiredLength);
        }

        if (errors["email"]) {
            return this.translationService.t("FormValidationService.Email");
        }

        if (errors["min"]) {
            return this.translationService.t("FormValidationService.Min", errors["min"].min);
        }

        if (errors["max"]) {
            return this.translationService.t("FormValidationService.Max", errors["max"].max);
        }

        // --- custom validators

        if (errors["number"]) {
            return this.translationService.t("FormValidationService.Number");
        }

        if (errors["minimumArrayLength"]) {
            return this.translationService.t("FormValidationService.MinimumArrayLength");
        }

        if (errors["duplicateArrayItem"]) {
            return this.translationService.t("FormValidationService.DuplicateArrayItem");
        }

        if (errors["emptyWhiteSpace"]) {
            return this.translationService.t("FormValidationService.EmptyWhiteSpace");
        }

        if (errors["emailAlreadyExists"]) {
            return this.translationService.t("FormValidationService.EmailAlreadyExists");
        }

        if (errors["configNameDuplicated"]) {
            return this.translationService.t("FormValidationService.ConfigNameDuplicated");
        }

        if (errors["groupNameDuplicated"]) {
            return this.translationService.t("FormValidationService.GroupNameDuplicated");
        }

        if (errors["roadSegmentNameDuplicated"]) {
            return this.translationService.t("FormValidationService.RoadSegmentNameDuplicated");
        }

        if (errors["vehicleDuplicated"]) {
            return this.translationService.t("FormValidationService.VehicleAlreadyExists");
        }

        if (errors["iccidFormat"]) {
            return this.translationService.t("FormValidationService.IccidFormat");
        }

        if (errors["iccidValid"]) {
            return this.translationService.t("FormValidationService.IccidValid");
        }

        if (errors["parentCascadeContainsDepot"]) {
            return this.translationService.t("FormValidationService.ParentCascadeContainsDepot");
        }

        if (errors["parentIsDepotAndSomeVehiclesAreInDepot"]) {
            return this.translationService.t("FormValidationService.ParentIsDepotAndSomeVehiclesAreInDepot");
        }

        if (errors["parentSameAsChild"]) {
            return this.translationService.t("FormValidationService.ParentSameAsChild");
        }

        if (errors["accountHasConfig"]) {
            return this.translationService.t("FormValidationService.AccountHasConfig")
        }

        if (errors["engineerCompanyNameDuplicated"]) {
            return this.translationService.t("FormValidationService.EngineerCompanyNameDuplicated")
        }

        // Todo allow translation keys to be passed
        if (errors["customMessage"]) {
            return errors["customMessage"];
        }

        console.warn("No error state given for", errors);

        return this.translationService.t("FormValidationService.Fallback");
    }

    controlHasErrors(control: NgControl): boolean {
        if (!control) {
            return false;
        }

        return control.errors && !!Object.keys(control.errors).length;
    }

    updateValueAndValidity(formGroup: FormGroup): void {

        if (!formGroup?.controls) {
            return;
        }

        for (let key in formGroup.controls) {
            formGroup.controls[key].updateValueAndValidity();
        }

        formGroup.markAllAsTouched();
        formGroup.updateValueAndValidity();
    }

    checkFormValid(formGroup: FormGroup): boolean {

        if (!formGroup) {
            return false;
        }

        this.updateValueAndValidity(formGroup);

        return formGroup.valid;
    }
}

export class CustomFormValidators {
    static number = (control: AbstractControl): ValidationErrors | null => {

        if (Number.isFinite(control.value)) {
            return null;
        }

        return {
            "number": { valid: false }
        };
    }

    static emptyWhiteSpace = (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        if (control.value.length && control.value.trim().length) {
            return null;
        }

        return {
            "emptyWhiteSpace": { valid: false }
        };
    }

    static minimumArrayLength = (minimum: number) => (control: AbstractControl): ValidationErrors | null => {
        if (control.value.length >= minimum) {
            return null;
        }

        return {
            "minimumArrayLength": { valid: false }
        };
    }

    static duplicateVehicle = (vehicles: IVehicle[]) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || control.value.length < 3 || !vehicles?.length) {
            return;
        }

        const matchingVehicle = vehicles.find(vehicle => {
            return vehicle.Reg.toLowerCase() === control.value.toLowerCase() || vehicle.ShortReg.toLowerCase() === control.value.toLowerCase();
        })

        if (matchingVehicle) {
            return { vehicleDuplicated: true };
        }

        return null;
    }

    static accountHasConfig = (accountIds: number[]) => (control: AbstractControl): ValidationErrors | null => {
        if (!accountIds?.length) {
            return;
        }

        if (!control.value) {
            return null;
        }

        const matchingAccountId = accountIds.find(a => {
            return accountIds.some(id => id === control.value.Id);
        })

        if (matchingAccountId) {
            return {
                "accountHasConfig": { valid: false }
            }
        }

        return null;
    }

    static duplicateEmail = (users: IUser[]) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || control.value.length < 3 || !users?.length) {
            return;
        }

        const matchingUser = users.find(user => {
            return user.Email.toLowerCase() === control.value.toLowerCase();
        })

        if (matchingUser) {
            return { emailAlreadyExists: true };
        }

        return null;
    }

    static duplicateConfigName = (configs: ICameraConfiguration[]) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || control.value.length < 3 || !configs?.length) {
            return;
        }

        const matchingConfig = configs.find(config => {
            return config.Name.toLowerCase() === control.value.toLowerCase();
        })

        if (matchingConfig) {
            return { configNameDuplicated: true };
        }

        return null;
    }

    static parentMustNotBeSameAsChild = (group: IGroupWithIds) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return;
        }

        if (group.Id === control.value) {
            return {
                parentSameAsChild: true
            };
        }

        return null;
    }

    static vehiclesMustNotBeInDepotIfParentsAreDepot = (group: IGroupWithIds, hierarchy: IGroupWithIds[]) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const parentGroup = GroupsHierarchyHelper.find(g => g.Id === control.value, hierarchy);
        const parents = GroupsHierarchyHelper.getParents(parentGroup, true);
        const depot = parents.find(x => x.IsDepot); // there may be 1 depot or 0 depots only

        if (!depot) {
            return null;
        }

        const anyVehicleInDifferentDepot = group.VehicleIds.some(vehicleId => {
            const anyDepotContainsVehicle = GroupsHierarchyHelper.some(
                g =>  g.IsDepot && g.Id !== depot.Id && g.VehicleIds.includes(vehicleId),
                hierarchy
            );

            return anyDepotContainsVehicle;
        });

        if (anyVehicleInDifferentDepot) {
            return {
                parentIsDepotAndSomeVehiclesAreInDepot: true
            }
        }

        return null;
    }
        

    static groupCanBeUsedAsParent = (group: IGroupWithIds, allGroups: IGroupWithIds[]) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        if (!group.IsDepot) {
            return null;
        }

        let prospectiveParentId = control.value;
        let anyCascadeGroupIsDepot = false;

        while (!!prospectiveParentId && !anyCascadeGroupIsDepot) {
            const parentGroup = allGroups.find(g => g.Id === prospectiveParentId);

            if (!parentGroup) {
                break;
            }

            anyCascadeGroupIsDepot = parentGroup.IsDepot;

            prospectiveParentId = parentGroup.ParentId;
        }
        
        if (anyCascadeGroupIsDepot) {
            return {
                "parentCascadeContainsDepot": { valid: false }
            };
        }

        return null;
    }

    static duplicateProperty = (items: any[], propertyName: string, customErrorKey: string) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || !items.length) {
            return;
        }

        const matchingItem = items.find(item => {
            return item[propertyName].toLowerCase() === control.value.toLowerCase();
        })

        if (matchingItem) {
            return { [customErrorKey]: true }
        }

        return null;
    }

    static duplicateArrayItem = (control: AbstractControl): ValidationErrors | null => {

        if (!control.value) {
            return null;
        }

        const formArrayControls: AbstractControl[] = control["_parent"]?.["controls"];

        if (!formArrayControls?.length) {
            return null;
        }

        const duplicateControls: AbstractControl[] = formArrayControls.filter(formArrayControl => {
            return formArrayControl !== control
                && formArrayControl.value === control.value
                && control.value !== "";
        });

        if (!duplicateControls?.length) {
            return null;
        }

        return {
            "duplicateArrayItem": { valid: false }
        };
    }

    static simIccid = (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        if (control.value.length < 19 || control.value.length > 20) {
            return {
                "iccidFormat": { valid: false }
            };
        }

        if (!luhnCheck(control.value)) {
            return {
                "iccidValid": { valid: false }
            };
        }

        return null;
    }

    static duplicateVehicleFromDatabaseAsync(service: FoundationVehiclesService, accountId: number): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            return service.doesVehicleExist(accountId, control.value).pipe(
                map(vehicleExists => {

                    if (vehicleExists) {
                        return {
                            vehicleDuplicated: true
                        };
                    }

                    return null;
                })
            );
        };
    }
}

function luhnCheck(iccid): boolean {
    let sum = 0;
    let val = iccid.substr(0, iccid.length - 1)
    let lastDigit = parseInt(iccid.substr(iccid.length - 1, 1))
    let parity = val.length % 2

    for (let i = val.length - 1; i >= 0; i--) {
        let intVal = parseInt(val.substr(i, 1));
        if (i % 2 != parity) {
            intVal *= 2;
            if (intVal > 9) {
                intVal = 1 + (intVal % 10);
            }
        }
        sum += intVal;
    }

    return ((sum * 9) % 10) === lastDigit;
}
